import { Button } from '@/components/Button/Button';
import { Chip } from '@/components/Chip/Chip';
import { Icon } from '@/components/Icon/Icon';
import type { InternalAccount } from '@/graphql/staff';
import { formatDay } from '@/lib/format';
import { personLabel, roleName } from '@/lib/staff';

import styles from './PeopleList.module.css';

export interface PeopleListProps {
  accounts: readonly InternalAccount[];
  /** The signed-in person, whose own row is never editable. */
  meId?: string | null;
  meEmail?: string | null;
  canEdit: boolean;
  onEditRoles: (account: InternalAccount) => void;
  onRemove: (account: InternalAccount) => void;
  /** Shown when the reader may change who manages which brokerage. */
  onBrokerages?: (account: InternalAccount) => void;
  /** How many brokerages each person manages, by person id. */
  brokerageCounts?: Readonly<Record<string, number>>;
}

/** Everyone admitted to the internal portal, with the roles each holds. */
export function PeopleList({
  accounts,
  meId,
  meEmail,
  canEdit,
  onEditRoles,
  onRemove,
  onBrokerages,
  brokerageCounts,
}: PeopleListProps) {
  return (
    <ul aria-label="People" className={styles.list}>
      {accounts.map((account) => {
        const isMe = Boolean(account.personId) && account.personId === meId;
        const label = personLabel(account, meId, meEmail);
        return (
          <li className={styles.row} key={account.id}>
            <span aria-hidden="true" className={`${styles.avatar} ${isMe ? styles.me : ''}`}>
              <Icon name={isMe ? 'check-circle' : 'lock-key'} />
            </span>
            <span className={styles.main}>
              <span className={styles.name}>{label}</span>
              <span className={styles.meta}>
                Added {formatDay(account.createdAt)}
                {brokerageCounts && account.personId && brokerageCounts[account.personId]
                  ? ` · Manages ${brokerageCounts[account.personId]} ${brokerageCounts[account.personId] === 1 ? 'brokerage' : 'brokerages'}`
                  : null}
              </span>
            </span>
            <span className={styles.roles}>
              {account.roles.length > 0 ? (
                account.roles.map((role) => (
                  <Chip key={role} tone={role === 'ADMIN' ? 'good' : 'neutral'}>
                    {roleName(role)}
                  </Chip>
                ))
              ) : (
                <Chip tone="warn">No role</Chip>
              )}
            </span>
            {canEdit ? (
              isMe ? (
                <span className={styles.note}>Ask another admin to change your access</span>
              ) : (
                <span className={styles.actions}>
                  {onBrokerages ? (
                    <Button
                      aria-label={`Brokerages for ${label}`}
                      onClick={() => onBrokerages(account)}
                      size="sm"
                      variant="ghost"
                    >
                      Brokerages
                    </Button>
                  ) : null}
                  <Button
                    aria-label={`Change roles for ${label}`}
                    onClick={() => onEditRoles(account)}
                    size="sm"
                    variant="ghost"
                  >
                    Change roles
                  </Button>
                  <Button
                    aria-label={`Remove ${label}`}
                    onClick={() => onRemove(account)}
                    size="sm"
                    variant="quiet"
                  >
                    Remove
                  </Button>
                </span>
              )
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
